import { useContext, useLayoutEffect, useState } from "react";
import CustomizerContext from "../../../../../../_helper/Customizer";
import {
  InventoryPriceGuide,
  InventoryGroup,
  InventoryClass,
  InventoryType,
  Amount,
  InventoryDescription,
  FiscalYear,
} from "../../../../../../Constant";

import { toast } from "react-toastify";
import useAuth from "../../../../../../Hooks/useAuth";
import useAxiosPrivate from "../../../../../../Hooks/useAxiosPrivate";
import { DisposalMethodApi, InventoryClassApi, InventoryDescriptionApi, InventoryDescPriceGuideByTypeApi, InventoryGroupApi, InventoryTypeApi } from "../../../../../../api";
import { createDropdownField } from "../../../../../../Data/FormField/PublicData";
import { InventoryPriceGuideResponse } from "../types";

export default function useInventoryForm() {
  const axiosPrivate = useAxiosPrivate();
  const formTitle = InventoryPriceGuide;
  const { userOrganisationId }: any = useAuth();
  const [formFields, setFormFields] = useState<any[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<InventoryPriceGuideResponse[]>([]);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [clearRowActive, setClearRowActive] = useState(false);

  const InventoryGroupDropDown = createDropdownField(
      axiosPrivate,
      "administration_inventory_group_id",
      "administration_inventory_group_id",
      InventoryGroup,
      `${InventoryGroupApi}?organisation_id=${userOrganisationId}`,
      "",
      6,
  );

  InventoryGroupDropDown.onChange = async (id, updatedFields) => {
      const nextField = updatedFields.find(
          (field: any) => field.id === "administration_inventory_class_id"
      );
      if (nextField) {
        const endPoint = `${InventoryClassApi}?inventory_group_id=${id}`
        await nextField.fetchOptions(id, false, endPoint);
          const updatedFormFields = updatedFields.map((field: any) => {
              if (field.id === "administration_inventory_group_id") {
                  return { ...field, value: id };
              }
              return field;
          });
          setFormFields([...updatedFormFields]);
      }
  };

  const InventoryClassDropDown = createDropdownField(
      axiosPrivate,
      "administration_inventory_class_id",
      "administration_inventory_class_id",
      InventoryClass,
      ``,
      "",
      6,
  );

  InventoryClassDropDown.onChange = async (id, updatedFields) => {
      const nextField = updatedFields.find(
          (field: any) => field.id === "administration_inventory_type_id"
      );
      if (nextField) {
        const endPoint = `${InventoryTypeApi}?inventory_class_id=${id}`
        await nextField.fetchOptions(id, false, endPoint);
          const updatedFormFields = updatedFields.map((field: any) => {
              if (field.id === "administration_inventory_class_id") {
                  return { ...field, value: id };
              }
              return field;
          });
          setFormFields([...updatedFormFields]);
      }
  };

  const InventoryTypeDropDown = createDropdownField(
      axiosPrivate,
      "administration_inventory_type_id",
      "administration_inventory_type_id",
      InventoryType,
      ``,
      "",
      6,
  );

  InventoryTypeDropDown.onChange = async (id, updatedFields) => {
      const nextField = updatedFields.find(
          (field: any) => field.id === "administration_inventory_description_id"
      );
      if (nextField) {
        const endPoint = `${InventoryDescriptionApi}?inventory_class_id=${id}`
        await nextField.fetchOptions(id, false, endPoint);
          const updatedFormFields = updatedFields.map((field: any) => {
              if (field.id === "administration_inventory_type_id") {
                  return { ...field, value: id };
              }
              return field;
          });
          setFormFields([...updatedFormFields]);
      }
  };

  // ?? Call on every dependent change
  async function updateFields() {

    const updatedFields = [
      InventoryGroupDropDown, 
      InventoryClassDropDown, 
      InventoryTypeDropDown,  
      ...formFields];

    await InventoryGroupDropDown.fetchOptions();
    setFormFields(updatedFields);
  }

  useLayoutEffect(() => {
    //?? Updates all fields once component is mounted
    updateFields();
  }, []);


  const { layoutURL } = useContext(CustomizerContext);
  const backBtn = `${process.env.PUBLIC_URL}/administration/procurement/budget-price-guides/inventories/${layoutURL}`;

  const fetchData = () => {
    setLoading(true);
    axiosPrivate
      .get(
        `${InventoryDescPriceGuideByTypeApi}?with_trashed=${false}&inventory_type_id=&fiscal_year_id=16`
      )
      .then((res) => {
        const response: InventoryPriceGuideResponse[] = res.data.data;
        setData(response);

        setLoading(false);
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          toast.error(err.response.data.detail);
        } else {
          toast.error("Whoops! Something went wrong");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useLayoutEffect(() => {
    fetchData();
  }, []);

  const onSubmit = async (data: any) => {
    data.organisation_id = userOrganisationId;
    // console.log(data);
    await axiosPrivate
      .post(DisposalMethodApi, data)
      .then((_res) => {
        toast.success(`${_res.data.data.name} Created Successfully.`);
      })
      .catch((err) => {
        if (err.response && err.response.status === 422) {
          toast.error("Validation Errors");
        } else if (err.response && err.response.status === 400) {
          toast.error(err.response.data.detail);
        } else {
          toast.error("Whoops! Something went wrong");
        }
        //toast.error(err.response.message || "Whoops! Something went wrong");
      });
  };

  const tableColumns = [
    {
      name: InventoryDescription,
      selector: (row: (typeof data)[0]) => `${row.administration_inventory_description.name}`,
      sortable: true,
      center: false,
    },
    {
      name: Amount,
      selector: (row: (typeof data)[0]) => `${row.amount}`,
      sortable: true,
      center: false,
    },
    {
      name: FiscalYear,
      selector: (row: (typeof data)[0]) => `${row.finance_fiscal_year.name}`,
      sortable: true,
      center: false,
    },
    {
      name: InventoryType,
      selector: (row: (typeof data)[0]) => `${row.administration_inventory_description.administration_inventory_type.name}`,
      sortable: true,
      center: false,
    },
    {
      name: InventoryClass,
      selector: (row: (typeof data)[0]) => `${row.administration_inventory_description.administration_inventory_type.administration_inventory_class.name}`,
      sortable: true,
      center: false,
    },
    {
      name: InventoryGroup,
      selector: (row: (typeof data)[0]) => `${row.administration_inventory_description.administration_inventory_type.administration_inventory_class.administration_inventory_group.name}`,
      sortable: true,
      center: false,
    },
  ];

  return { formTitle, 
    formFields, 
    backBtn, 
    onSubmit, 
    data,
    tableColumns,
    clearRowActive,
    setClearRowActive,
    setSelectedItems, };
}
